import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { translations, LANGUAGES } from "./translations";

const I18nContext = createContext(null);

// Default Lek-per-EUR rate (used until we successfully fetch the BoA value).
// Stored on storefront load and refreshed every hour.
const DEFAULT_LEK_PER_EUR = 100;
const FX_REFRESH_MS = 60 * 60 * 1000; // 1 hour
const SYMBOLS = { ALL: "L", EUR: "€" };

const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export const I18nProvider = ({ children }) => {
    const [lang, setLang] = useState(() => {
        const saved = localStorage.getItem("bb_lang");
        if (saved && LANGUAGES.some((l) => l.id === saved)) return saved;
        return "en";
    });
    const [currency, setCurrency] = useState(
        () => localStorage.getItem("bb_currency") || "ALL",
    );

    // Lek per 1 EUR (e.g. 94.96). Seeded from localStorage so the very first
    // paint shows a sensible value while the live fetch is in flight.
    const [lekPerEur, setLekPerEur] = useState(() => {
        const cached = parseFloat(localStorage.getItem("bb_lek_per_eur") || "");
        return Number.isFinite(cached) && cached > 0 ? cached : DEFAULT_LEK_PER_EUR;
    });

    useEffect(() => {
        localStorage.setItem("bb_lang", lang);
        document.documentElement.lang = lang;
    }, [lang]);

    useEffect(() => {
        localStorage.setItem("bb_currency", currency);
    }, [currency]);

    // Fetch live EUR/ALL rate from our backend (which scrapes BoA + caches).
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const res = await fetch(`${apiBase}/api/fx/rates`);
                if (!res.ok) return;
                const data = await res.json();
                const rate = data?.rates?.EUR;
                if (cancelled || !Number.isFinite(rate) || rate <= 0) return;
                setLekPerEur(rate);
                localStorage.setItem("bb_lek_per_eur", String(rate));
            } catch {
                /* keep cached / default rate */
            }
        };
        load();
        const id = setInterval(load, FX_REFRESH_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    const t = useCallback(
        (key) => {
            const dict = translations[lang] || translations.en;
            return dict[key] ?? translations.en[key] ?? key;
        },
        [lang],
    );

    const formatPrice = useCallback(
        (priceInALL) => {
            const allValue = priceInALL;
            const eurValue = lekPerEur > 0 ? priceInALL / lekPerEur : 0;
            const localeMap = { sq: "sq-AL", en: "en-US", hi: "hi-IN", it: "it-IT", el: "el-GR", fr: "fr-FR", mk: "mk-MK" };
            const loc = localeMap[lang] || "en-US";
            const fmtALL = `L ${Math.round(allValue).toLocaleString(loc)}`;
            const fmtEUR = `€${eurValue.toFixed(2)}`;
            if (currency === "EUR") return { primary: fmtEUR, secondary: fmtALL };
            return { primary: fmtALL, secondary: fmtEUR };
        },
        [currency, lang, lekPerEur],
    );

    const value = useMemo(
        () => ({
            lang,
            setLang,
            currency,
            setCurrency,
            symbol: SYMBOLS[currency],
            lekPerEur,
            t,
            formatPrice,
            LANGUAGES,
        }),
        [lang, currency, lekPerEur, t, formatPrice],
    );

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    );
};

export const useI18n = () => {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useI18n must be used within I18nProvider");
    return ctx;
};
